import { defineStore } from 'pinia'
import { ref } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import type { User } from '@supabase/supabase-js'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)

  async function initialize() {
    try {
      // Get initial session
      const { data: { session } } = await supabase.auth.getSession()
      user.value = session?.user ?? null

      // Store access token for API calls
      if (session?.access_token) {
        localStorage.setItem('access_token', session.access_token)
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange((_event, session) => {
        user.value = session?.user ?? null

        // Update stored token
        if (session?.access_token) {
          localStorage.setItem('access_token', session.access_token)
        } else {
          localStorage.removeItem('access_token')
        }
      })
    } finally {
      loading.value = false
    }
  }

  async function login(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) throw error

    // Check if user is admin.
    // This asks the backend rather than querying `admin_users` over PostgREST:
    // the data API is not exposed to browsers since the move to self-hosted
    // PostgreSQL. A 200 here means the token verified AND the email is in
    // admin_users, which is what require_admin enforces server-side.
    const token = data.session?.access_token
    const meResponse = await fetch(`/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (!meResponse.ok) {
      await logout()
      throw new Error('Unauthorized access')
    }

    user.value = data.user

    // Store access token for API calls
    if (data.session?.access_token) {
      localStorage.setItem('access_token', data.session.access_token)
    }

    return data
  }

  async function logout() {
    await supabase.auth.signOut()
    user.value = null
    // Clear any stored tokens
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    // Clear cached data to force refresh on next login
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('institutions_') || key.startsWith('content_') || key.startsWith('emails_')) {
        localStorage.removeItem(key)
      }
    })
  }

  return {
    user,
    loading,
    initialize,
    login,
    logout
  }
}) 